import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight, Check, ExternalLink, Calendar, DollarSign, User, Clock } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useTheme } from '../contexts/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Checklist item definitions with all metadata
const CHECKLIST_SECTIONS = [
  {
    id: 'pre_event_setup',
    title: 'Pre-Event Setup',
    columnName: 'Pre-Event Setup',
    items: [
      { key: 'logos_on_site', label: 'Logos on Site', fields: [] },
      { key: 'event_date', label: 'Event Date', fields: ['eventDateAuto'], linkToTab: null },
      { key: 'speaker_confirmed', label: 'Speaker Confirmed', fields: [], linkToTab: 'qsecdef-event' },
      { key: 'press_release_written', label: 'Press Release Written', fields: ['url'], urlLabel: 'Google Drive Link' },
      { key: 'pr_release_date', label: 'PR Release Date', fields: ['scheduled_date'], dateLabel: 'Scheduled Date' },
    ]
  },
  {
    id: 'content_creation',
    title: 'Content Creation',
    columnName: 'Content Creation',
    items: [
      { key: 'article_header_image', label: 'Article Header Image', fields: ['url'], urlLabel: 'Google Drive Link' },
      { key: 'speaker_bio_on_site', label: 'Speaker Bio On Site', fields: ['url'], urlLabel: 'URL Link' },
    ]
  },
  {
    id: 'event_marketing',
    title: 'Event Marketing Activities',
    columnName: 'Event Marketing Activities',
    items: [
      { key: 'linkedin_event_created', label: 'LinkedIn Event Created', fields: ['url'], urlLabel: 'Event URL' },
      { key: 'event_on_linkedin_newsletter', label: 'Event On LinkedIn Newsletter', fields: [] },
      { key: 'event_connected_to_zapier', label: 'Event Connected to Zapier', fields: ['url'], urlLabel: 'Zap URL' },
      { key: 'brevo_newsletter', label: 'Brevo Newsletter', fields: [] },
      { key: 'linkedin_pre_event_advertising', label: 'LinkedIn Pre Event Advertising', fields: ['url', 'budget_usd'], urlLabel: 'Campaign URL', requiresBudget: true },
      { key: 'events_com_listing', label: 'Events.com Listing', fields: ['url'], urlLabel: 'Event URL' },
      { key: 'event_brite_listing', label: 'Event Brite Listing', fields: ['url'], urlLabel: 'Event URL' },
    ]
  },
  {
    id: 'event_recording',
    title: 'Event & Recording',
    columnName: 'Event & Recording',
    items: [
      { key: 'event_recorded', label: 'Event Recorded', fields: ['url', 'url2'], urlLabel: 'Recording URL', url2Label: 'Transcript URL' },
    ]
  },
  {
    id: 'video_production',
    title: 'Video Production',
    columnName: 'Video Production',
    items: [
      { key: 'long_video_edited', label: 'Long Video Edited', fields: ['url'], urlLabel: 'Drive URL' },
      { key: 'short_video_edited', label: 'Short Video Edited', fields: ['url'], urlLabel: 'Drive URL' },
      { key: 'social_1_edited', label: 'Social 1 Edited', fields: ['url'], urlLabel: 'Drive URL' },
      { key: 'social_2_edited', label: 'Social 2 Edited', fields: ['url'], urlLabel: 'Drive URL' },
      { key: 'social_3_edited', label: 'Social 3 Edited', fields: ['url'], urlLabel: 'Drive URL' },
    ]
  },
  {
    id: 'distribution',
    title: 'Distribution',
    columnName: 'Distribution',
    items: [
      { key: 'posted_qsecdef_lecture', label: 'Posted QSECDEF Lecture', fields: ['url'], urlLabel: 'URL' },
      { key: 'uploaded_to_youtube', label: 'Uploaded to YouTube', fields: ['url'], urlLabel: 'YouTube URL' },
      { key: 'article_created_published', label: 'Article Created & Published', fields: ['url'], urlLabel: 'Website URL' },
      { key: 'short_video_on_speaker_bio', label: 'Short Video on Speaker Bio', fields: ['url'], urlLabel: 'URL', autoFromKey: 'speaker_bio_on_site' },
      { key: 'socials_scheduled_linkedin', label: 'Socials Scheduled LinkedIn', fields: [] },
      { key: 'socials_scheduled_youtube', label: 'Socials Scheduled YouTube', fields: [] },
      { key: 'socials_scheduled_instagram', label: 'Socials Scheduled Instagram', fields: ['url'], urlLabel: 'Post URL' },
      { key: 'socials_scheduled_facebook', label: 'Socials Scheduled Facebook', fields: ['url'], urlLabel: 'Post URL' },
      { key: 'socials_scheduled_tiktok', label: 'Socials Scheduled TikTok', fields: ['url'], urlLabel: 'Post URL' },
      { key: 'socials_scheduled_xcom', label: 'Socials Scheduled X.com', fields: ['url'], urlLabel: 'Post URL' },
    ]
  },
  {
    id: 'advertising',
    title: 'Advertising',
    columnName: 'Advertising',
    items: [
      { key: 'social_ads_linkedin', label: 'Social Ads LinkedIn', fields: ['url', 'budget_usd'], urlLabel: 'Campaign URL', requiresBudget: true },
      { key: 'social_ads_youtube', label: 'Social Ads YouTube', fields: ['url', 'budget_usd'], urlLabel: 'Campaign URL', requiresBudget: true },
      { key: 'social_ads_instagram', label: 'Social Ads Instagram', fields: ['url', 'budget_usd'], urlLabel: 'Campaign URL', requiresBudget: true },
      { key: 'social_ads_facebook', label: 'Social Ads Facebook', fields: ['url', 'budget_usd'], urlLabel: 'Campaign URL', requiresBudget: true },
      { key: 'social_ads_tiktok', label: 'Social Ads TikTok', fields: ['url', 'budget_usd'], urlLabel: 'Campaign URL', requiresBudget: true },
      { key: 'social_ads_xcom', label: 'Social Ads X.com', fields: ['url', 'budget_usd'], urlLabel: 'Campaign URL', requiresBudget: true },
    ]
  }
];

// Budget required items
const BUDGET_REQUIRED_ITEMS = [
  'linkedin_pre_event_advertising',
  'social_ads_linkedin',
  'social_ads_youtube',
  'social_ads_instagram',
  'social_ads_facebook',
  'social_ads_tiktok',
  'social_ads_xcom'
];

const MediaChecklist = ({ card, onUpdate, onTabChange, eventDate }) => {
  const { isDark } = useTheme();
  const [checklist, setChecklist] = useState(card?.media_checklist || {});
  const [expandedSections, setExpandedSections] = useState({
    pre_event_setup: false,
    content_creation: false,
    event_marketing: false,
    event_recording: false,
    video_production: false,
    distribution: false,
    advertising: false
  });
  const [saving, setSaving] = useState(null);
  const [budgetModal, setBudgetModal] = useState(null);
  const [tempBudget, setTempBudget] = useState('');
  const [tempUrl, setTempUrl] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setChecklist(card?.media_checklist || {});
  }, [card?.media_checklist]);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const getItemData = (key) => {
    return checklist[key] || {};
  };

  const calculateProgress = () => {
    let completed = 0;
    let total = 0;
    CHECKLIST_SECTIONS.forEach(section => {
      section.items.forEach(item => {
        total++;
        if (getItemData(item.key).completed) {
          completed++;
        }
      });
    });
    return { completed, total, percentage: total > 0 ? Math.round((completed / total) * 100) : 0 };
  };

  const calculateTotalBudget = () => {
    let total = 0;
    BUDGET_REQUIRED_ITEMS.forEach(key => {
      const itemData = getItemData(key);
      if (itemData.budget_usd) {
        total += parseFloat(itemData.budget_usd) || 0;
      }
    });
    return total;
  };

  const handleCheckboxClick = async (item, currentData) => {
    if (item.requiresBudget && !currentData.completed) {
      // Show budget modal for advertising items
      setBudgetModal(item);
      setTempBudget(currentData.budget_usd?.toString() || '');
      setTempUrl(currentData.url || '');
      return;
    }

    await updateChecklistItem(item.key, {
      completed: !currentData.completed,
      url: currentData.url,
      url2: currentData.url2,
      scheduled_date: currentData.scheduled_date,
      budget_usd: currentData.budget_usd
    });
  };

  const handleBudgetSubmit = async () => {
    if (!budgetModal) return;
    
    const budget = parseFloat(tempBudget);
    if (isNaN(budget) || budget <= 0) {
      alert('Please enter a valid budget amount');
      return;
    }

    await updateChecklistItem(budgetModal.key, {
      completed: true,
      url: tempUrl,
      budget_usd: budget
    });

    setBudgetModal(null);
    setTempBudget('');
    setTempUrl('');
  };

  const updateChecklistItem = async (itemKey, data) => {
    setSaving(itemKey);
    try {
      const response = await fetch(`${BACKEND_URL}/api/cards/${card.card_id}/media-checklist`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          item_key: itemKey,
          ...data
        })
      });

      if (response.ok) {
        const updatedCard = await response.json();
        setChecklist(updatedCard.media_checklist || {});
        setHasChanges(true);
        // Don't call onUpdate here - it causes the modal to close
        // The board will refresh when the modal is closed
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to update checklist');
      }
    } catch (error) {
      console.error('Update checklist error:', error);
      alert('Failed to update checklist');
    } finally {
      setSaving(null);
    }
  };

  const handleFieldChange = async (itemKey, field, value) => {
    const currentData = getItemData(itemKey);
    await updateChecklistItem(itemKey, {
      completed: currentData.completed,
      url: field === 'url' ? value : currentData.url,
      url2: field === 'url2' ? value : currentData.url2,
      scheduled_date: field === 'scheduled_date' ? value : currentData.scheduled_date,
      budget_usd: field === 'budget_usd' ? parseFloat(value) || 0 : currentData.budget_usd
    });
  };

  const progress = calculateProgress();
  const totalBudget = calculateTotalBudget();
  const estimatedClicks = totalBudget > 0 ? Math.round(totalBudget / 0.15) : 0;

  const getSectionProgress = (section) => {
    let completed = 0;
    section.items.forEach(item => {
      if (getItemData(item.key).completed) completed++;
    });
    return { completed, total: section.items.length };
  };

  return (
    <div className="space-y-4">
      {/* Progress Bar */}
      <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-4`}>
        <div className="flex items-center justify-between mb-2">
          <span className={`${isDark ? 'text-white' : 'text-gray-900'} font-medium`}>Overall Progress</span>
          <span className={`${isDark ? 'text-[#A2FF00]' : 'text-green-600'} font-bold`}>{progress.completed} of {progress.total} completed</span>
        </div>
        <div className={`w-full ${isDark ? 'bg-[#0A0A0A]' : 'bg-gray-200'} rounded-full h-3`}>
          <div
            className={`${isDark ? 'bg-[#A2FF00]' : 'bg-green-500'} h-3 rounded-full transition-all duration-300`}
            style={{ width: `${progress.percentage}%` }}
          />
        </div>
        <div className={`text-center ${isDark ? 'text-gray-400' : 'text-gray-500'} text-sm mt-1`}>{progress.percentage}%</div>
      </div>

      {/* Budget Summary */}
      <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-4`}>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className={`${isDark ? 'text-gray-400' : 'text-gray-500'} text-sm`}>Total Ad Budget</div>
            <div className={`${isDark ? 'text-[#A2FF00]' : 'text-green-600'} text-2xl font-bold`}>${totalBudget.toLocaleString()}</div>
          </div>
          <div>
            <div className={`${isDark ? 'text-gray-400' : 'text-gray-500'} text-sm`}>Estimated Clicks</div>
            <div className={`${isDark ? 'text-white' : 'text-gray-900'} text-2xl font-bold`}>{estimatedClicks.toLocaleString()}</div>
          </div>
        </div>
      </div>

      {/* Sections */}
      {CHECKLIST_SECTIONS.map(section => {
        const sectionProgress = getSectionProgress(section);
        const isExpanded = expandedSections[section.id];
        const isComplete = sectionProgress.completed === sectionProgress.total;

        return (
          <div key={section.id} className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-sm overflow-hidden`}>
            {/* Section Header */}
            <button
              onClick={() => toggleSection(section.id)}
              className={`w-full p-4 flex items-center justify-between ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'} transition-colors`}
            >
              <div className="flex items-center gap-3">
                {isExpanded ? (
                  <ChevronDown className={`w-5 h-5 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
                ) : (
                  <ChevronRight className={`w-5 h-5 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
                )}
                <span className={`${isDark ? 'text-[#A2FF00]' : 'text-green-600'} font-medium`}>{section.title}</span>
                {isComplete && (
                  <span className={`${isDark ? 'bg-[#A2FF00]/20 text-[#A2FF00]' : 'bg-green-100 text-green-600'} text-xs px-2 py-1 rounded`}>Complete</span>
                )}
              </div>
              <span className={`${isDark ? 'text-gray-400' : 'text-gray-500'} text-sm`}>
                {sectionProgress.completed}/{sectionProgress.total}
              </span>
            </button>

            {/* Section Items */}
            {isExpanded && (
              <div className={`border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                {section.items.map(item => {
                  const itemData = getItemData(item.key);
                  const isItemSaving = saving === item.key;

                  return (
                    <div key={item.key} className={`p-4 border-b ${isDark ? 'border-white/5' : 'border-gray-100'} last:border-b-0`}>
                      <div className="flex items-start gap-3">
                        {/* Checkbox */}
                        <button
                          onClick={() => handleCheckboxClick(item, itemData)}
                          disabled={isItemSaving}
                          className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                            itemData.completed
                              ? isDark ? 'bg-[#A2FF00] border-[#A2FF00]' : 'bg-green-500 border-green-500'
                              : isDark ? 'border-gray-500 hover:border-[#A2FF00]' : 'border-gray-300 hover:border-green-500'
                          } ${isItemSaving ? 'opacity-50' : ''}`}
                        >
                          {itemData.completed && <Check className={`w-4 h-4 ${isDark ? 'text-black' : 'text-white'}`} />}
                        </button>

                        <div className="flex-1 min-w-0">
                          {/* Item Label */}
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`font-medium ${itemData.completed ? 'text-gray-400 line-through' : isDark ? 'text-white' : 'text-gray-900'}`}>
                              {item.label}
                            </span>
                            {item.linkToTab && (
                              <button
                                onClick={() => onTabChange && onTabChange(item.linkToTab)}
                                className={`${isDark ? 'text-[#A2FF00]' : 'text-green-600'} text-xs hover:underline`}
                              >
                                View Webinar →
                              </button>
                            )}
                            {item.key === 'event_date' && eventDate && (
                              <span className={`${isDark ? 'text-[#A2FF00]' : 'text-green-600'} text-sm`}>
                                ({new Date(eventDate).toLocaleDateString()})
                              </span>
                            )}
                          </div>

                          {/* Completion Info */}
                          {itemData.completed && itemData.delivered_by_name && (
                            <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {itemData.delivered_by_name}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {itemData.completed_date ? new Date(itemData.completed_date).toLocaleString() : '-'}
                              </span>
                            </div>
                          )}

                          {/* URL Fields */}
                          {item.fields.includes('url') && (
                            <div className="mt-2">
                              <label className="text-xs text-gray-500 block mb-1">{item.urlLabel || 'URL'}</label>
                              <div className="flex gap-2">
                                <input
                                  type="url"
                                  value={itemData.url || ''}
                                  onChange={(e) => handleFieldChange(item.key, 'url', e.target.value)}
                                  placeholder="https://..."
                                  className={`flex-1 ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border rounded text-sm px-3 py-1.5 focus:border-[#A2FF00] outline-none`}
                                />
                                {itemData.url && (
                                  <a
                                    href={itemData.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`p-1.5 ${isDark ? 'text-[#A2FF00] hover:bg-[#A2FF00]/10' : 'text-green-600 hover:bg-green-50'} rounded`}
                                  >
                                    <ExternalLink className="w-4 h-4" />
                                  </a>
                                )}
                              </div>
                            </div>
                          )}

                          {/* URL2 Field (for Event Recorded) */}
                          {item.fields.includes('url2') && (
                            <div className="mt-2">
                              <label className="text-xs text-gray-500 block mb-1">{item.url2Label || 'URL 2'}</label>
                              <div className="flex gap-2">
                                <input
                                  type="url"
                                  value={itemData.url2 || ''}
                                  onChange={(e) => handleFieldChange(item.key, 'url2', e.target.value)}
                                  placeholder="https://..."
                                  className={`flex-1 ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border rounded text-sm px-3 py-1.5 focus:border-[#A2FF00] outline-none`}
                                />
                                {itemData.url2 && (
                                  <a
                                    href={itemData.url2}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`p-1.5 ${isDark ? 'text-[#A2FF00] hover:bg-[#A2FF00]/10' : 'text-green-600 hover:bg-green-50'} rounded`}
                                  >
                                    <ExternalLink className="w-4 h-4" />
                                  </a>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Scheduled Date Field - Use Calendar Picker */}
                          {item.fields.includes('scheduled_date') && (
                            <div className="mt-2">
                              <label className="text-xs text-gray-500 block mb-1">{item.dateLabel || 'Date'}</label>
                              <div className="relative">
                                <DatePicker
                                  selected={itemData.scheduled_date ? new Date(itemData.scheduled_date) : null}
                                  onChange={(date) => handleFieldChange(item.key, 'scheduled_date', date ? date.toISOString().split('T')[0] : '')}
                                  dateFormat="dd/MM/yyyy"
                                  placeholderText="Select date"
                                  showPopperArrow={false}
                                  className={`${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border rounded text-sm px-3 py-1.5 focus:border-[#A2FF00] outline-none cursor-pointer w-full`}
                                  wrapperClassName="w-48"
                                  popperClassName="qsecdef-calendar-popper"
                                  showMonthDropdown
                                  showYearDropdown
                                  dropdownMode="select"
                                  isClearable
                                />
                                <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                              </div>
                            </div>
                          )}

                          {/* Budget Field (display only for completed items) */}
                          {item.fields.includes('budget_usd') && itemData.budget_usd && (
                            <div className="mt-2 flex items-center gap-2">
                              <DollarSign className={`w-4 h-4 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
                              <span className={`${isDark ? 'text-[#A2FF00]' : 'text-green-600'} font-medium`}>${itemData.budget_usd.toLocaleString()}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* Budget Modal */}
      {budgetModal && (
        <div className={`fixed inset-0 ${isDark ? 'bg-black/50' : 'bg-black/30'} flex items-center justify-center z-50`}>
          <div className={`${isDark ? 'bg-[#111] border-white/20' : 'bg-white border-gray-200'} border rounded-lg p-6 w-full max-w-md`}>
            <h3 className={`${isDark ? 'text-white' : 'text-gray-900'} text-lg font-bold mb-4`}>Enter Advertising Details</h3>
            <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'} text-sm mb-4`}>
              Budget is required for: <span className={isDark ? 'text-white' : 'text-gray-900'}>{budgetModal.label}</span>
            </p>
            
            <div className="space-y-4">
              <div>
                <label className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} block mb-1`}>Campaign URL</label>
                <input
                  type="url"
                  value={tempUrl}
                  onChange={(e) => setTempUrl(e.target.value)}
                  placeholder="https://..."
                  className={`w-full ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border rounded px-3 py-2 focus:border-[#A2FF00] outline-none`}
                />
              </div>
              
              <div>
                <label className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} block mb-1`}>Budget (USD) *</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input
                    type="number"
                    value={tempBudget}
                    onChange={(e) => setTempBudget(e.target.value)}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    className={`w-full ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border rounded pl-8 pr-3 py-2 focus:border-[#A2FF00] outline-none`}
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setBudgetModal(null);
                  setTempBudget('');
                  setTempUrl('');
                }}
                className={`flex-1 ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'} px-4 py-2 rounded transition-colors`}
              >
                Cancel
              </button>
              <button
                onClick={handleBudgetSubmit}
                className={`flex-1 ${isDark ? 'bg-[#A2FF00] hover:bg-[#8EFF00]' : 'bg-green-500 hover:bg-green-600'} text-black font-bold px-4 py-2 rounded transition-colors`}
              >
                Complete Task
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaChecklist;
